// When a contract was sent and signed, for the contract panel.
//
// The contract record's own timestamps come back only from the send/sign action
// responses — the admin detail response does not carry them (verified against
// ProspectAdminController.toDetailResponse), so after a reload they are gone and
// the panel would show "—" on a contract it knows was signed. The recorded state
// transitions carry the same moments durably, so they are the fallback.

import { OnboardingState, StateTransition } from "../types/onboarding";

export interface ContractTimestamps {
  sentAt: string | null;
  signedAt: string | null;
  timeToSignHours: number | null;
}

interface ContractTimestampSource {
  transitions?: StateTransition[];
  sent_at?: string | null;
  signed_at?: string | null;
  time_to_sign_hours?: number | null;
}

// Sending is recorded as CONTRACT_SENT on the manual/hub tracks and as
// ESIGN_AWAITED on the digital one, whichever the prospect went through.
const SENT_STATES: OnboardingState[] = ["CONTRACT_SENT", "ESIGN_AWAITED"];
const SIGNED_STATES: OnboardingState[] = ["CONTRACT_SIGNED"];

const MS_PER_HOUR = 1000 * 60 * 60;

const parse = (iso?: string | null): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return isNaN(ms) ? null : ms;
};

// Earliest recorded transition into any of the given states — a prospect can be
// sent a contract more than once (re-send, re-sign), and the first attempt is
// the moment the contract left.
const earliestTransition = (
  transitions: StateTransition[],
  states: OnboardingState[]
): string | null => {
  let best: { iso: string; ms: number } | null = null;
  transitions.forEach((t) => {
    if (!states.includes(t.state)) return;
    const ms = parse(t.transitioned_at);
    if (ms === null) return;
    if (!best || ms < best.ms) best = { iso: t.transitioned_at, ms };
  });
  return best ? (best as { iso: string }).iso : null;
};

export const contractTimestamps = (
  source: ContractTimestampSource
): ContractTimestamps => {
  const transitions = source.transitions ?? [];

  const sentAt =
    source.sent_at || earliestTransition(transitions, SENT_STATES) || null;
  const signedAt =
    source.signed_at || earliestTransition(transitions, SIGNED_STATES) || null;

  let timeToSignHours =
    source.time_to_sign_hours != null ? source.time_to_sign_hours : null;
  if (timeToSignHours === null) {
    const from = parse(sentAt);
    const to = parse(signedAt);
    if (from !== null && to !== null && to >= from) {
      timeToSignHours = (to - from) / MS_PER_HOUR;
    }
  }

  return { sentAt, signedAt, timeToSignHours };
};
